import axios from 'axios'
import { storage, SESSION_API_BASE_URL } from 'utils'

/**
 * The platform's half of the server-side session (API: `user_sessions`, migration 0143).
 *
 * WHAT LIVES WHERE
 *  - The long-lived session credential is an HttpOnly cookie the API sets on sign-in and
 *    rotates on every renewal. This code never sees it — page JavaScript cannot read it,
 *    so an XSS cannot steal it. It is only ever sent to `/api/auth/session/*`, on the
 *    SAME-SITE API host (`SESSION_API_BASE_URL` — see utils/apiUrl.ts for why the build's
 *    `*.run.app` URL cannot carry it).
 *  - The ACCESS token stays where it always was (`accessToken`, js-cookie / localStorage,
 *    sent as `X-Vera-Token`) but now lives ~15 minutes instead of 24 hours. Moving it to
 *    an HttpOnly cookie too would mean cookie-authenticating every API route plus CSRF
 *    tokens on every mutation and rewriting every direct reader of `accessToken` (Ask's
 *    fetch, subdomain checks, error reporter, dev session) — a much larger change. A short
 *    TTL bounds what a stolen access token is worth instead.
 *
 * Renewal is DEMAND-DRIVEN: a 401 (or a page load with no access token) triggers one
 * renewal. There is deliberately no background timer — an open but unused tab must not keep
 * a session alive, or the 7-day idle bound would never bite.
 */

const SESSION_HEADER = { 'X-Vera-Session': '1' }

/**
 * The API guards' 401 message for a revoked / expired session (auth.guard.ts and
 * auth-admin.guard.ts). A revoked session's access token can still be inside its 15
 * minutes, so expiry alone cannot detect "this device was signed out".
 */
export const SESSION_ENDED_MESSAGE = 'Session has been signed out'

/**
 * True when there is no access token, it cannot be read, or its `exp` has passed (with a
 * small skew allowance). Used to tell "my session is over" apart from an endpoint that
 * answers 401 for its own reasons (e.g. "current password is invalid") while the token is
 * perfectly valid — the latter must not sign the user out.
 */
export const isAccessTokenExpired = (skewSeconds = 5): boolean => {
  const token = storage.getItem('accessToken')
  if (!token) return true
  try {
    const payload = JSON.parse(
      atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number }
    if (typeof payload.exp !== 'number') return false
    return payload.exp * 1000 <= Date.now() + skewSeconds * 1000
  } catch {
    return true
  }
}

/**
 * What a renewal attempt concluded. The distinction is the point:
 *  - `renewed`     — a usable access token (fresh from the API, or one another tab just got);
 *  - `ended`       — the session is over (401): sign in again;
 *  - `unavailable` — the API could not answer (5xx, network). NOT a sign-out: a deploy or a
 *                    database blip must not log out everyone who happens to renew during it.
 */
export type RenewalResult =
  | { status: 'renewed'; token: string }
  | { status: 'ended' }
  | { status: 'unavailable' }

const LOCK_NAME = 'vera-platform-session-renewal'
/** Upper bound on one renewal request (it holds the cross-tab lock while in flight). */
export const RENEWAL_TIMEOUT_MS = 15_000
const LEASE_KEY = 'vera-platform-session-renewal-lease'
const LEASE_MS = 5000

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Run `fn` while holding a lock shared by every tab of this origin, so tabs renew ONE at a
 * time. Combined with re-reading the shared access token after acquiring it, a browser
 * reopening with five platform tabs makes one renewal, not five.
 *
 * `navigator.locks` (every supported browser) is the primary mechanism. Where it is missing,
 * a best-effort localStorage lease (5s, self-expiring) approximates it; and the API itself
 * tolerates concurrent renewals with the same cookie (a short grace window that answers
 * without rotating), so the fallback is an optimisation, not the correctness guarantee.
 */
const withCrossTabLock = async <T,>(fn: () => Promise<T>): Promise<T> => {
  const locks = (globalThis as { navigator?: { locks?: { request: (n: string, f: () => Promise<T>) => Promise<T> } } })
    .navigator?.locks
  if (locks?.request) return locks.request(LOCK_NAME, fn)

  const me = `${Date.now()}-${Math.random()}`
  const deadline = Date.now() + LEASE_MS
  try {
    while (Date.now() < deadline) {
      const raw = localStorage.getItem(LEASE_KEY)
      const lease = raw ? (JSON.parse(raw) as { owner: string; until: number }) : null
      if (!lease || lease.until < Date.now()) {
        localStorage.setItem(LEASE_KEY, JSON.stringify({ owner: me, until: Date.now() + LEASE_MS }))
        if (JSON.parse(localStorage.getItem(LEASE_KEY) ?? '{}').owner === me) break
      }
      await sleep(100)
    }
  } catch {
    // Storage unavailable: run unlocked (the API grace window still holds).
  }
  try {
    return await fn()
  } finally {
    try {
      if (JSON.parse(localStorage.getItem(LEASE_KEY) ?? '{}').owner === me) localStorage.removeItem(LEASE_KEY)
    } catch {
      /* ignore */
    }
  }
}

/** A still-valid access token in the shared store that differs from the one that failed. */
const freshSharedToken = (staleToken?: string | null): string | null => {
  const current = storage.getItem('accessToken')
  return current && current !== staleToken && !isAccessTokenExpired() ? String(current) : null
}

let inFlight: Promise<RenewalResult> | null = null

/**
 * Renew the session from the HttpOnly cookie.
 *
 * `staleToken` is the access token the caller just saw rejected. After taking the
 * cross-tab lock, if the shared store already holds a DIFFERENT, unexpired token (another
 * tab renewed while we waited), that one is used and no request is made.
 *
 * Single-flight within the tab: concurrent 401s share one attempt.
 */
export const renewPlatformSession = (staleToken?: string | null): Promise<RenewalResult> => {
  if (inFlight) return inFlight
  inFlight = (async (): Promise<RenewalResult> => {
    try {
      return await withCrossTabLock(async (): Promise<RenewalResult> => {
        const shared = freshSharedToken(staleToken)
        if (shared) return { status: 'renewed', token: shared }
        try {
          const response = await axios.post(
            `${SESSION_API_BASE_URL}/auth/session/refresh`,
            {},
            // Bounded: this request runs while holding the cross-tab lock, and a hung socket
            // would otherwise stall renewal in every tab of this browser.
            { withCredentials: true, headers: SESSION_HEADER, timeout: RENEWAL_TIMEOUT_MS },
          )
          const token: string | undefined = response.data?.tokens?.accessToken
          if (!token) return { status: 'unavailable' }
          storage.setItem('accessToken', token)
          return { status: 'renewed', token }
        } catch (error) {
          const status = axios.isAxiosError(error) ? error.response?.status : undefined
          // 404: the renewal route does not exist on this API (an older deploy, or a misrouted
          // base URL). There is no session to renew here — treat as ended, i.e. sign in.
          if (status === 404) return { status: 'ended' }
          if (status === 401) {
            // `SESSION_UNKNOWN`: our cookie was one another tab had already rotated past.
            // That tab may have stored a fresh token meanwhile — use it if so.
            const after = freshSharedToken(staleToken)
            if (after) return { status: 'renewed', token: after }
            return { status: 'ended' }
          }
          return { status: 'unavailable' }
        }
      })
    } finally {
      // Released as soon as it settles; callers already awaiting it share the result.
      inFlight = null
    }
  })()
  return inFlight
}

/**
 * Sign-out: revoke this session server-side and clear its cookie. Best-effort — local state
 * is cleared by the caller regardless — but it is what actually stops this device's session.
 */
export const endPlatformSession = async (): Promise<void> => {
  const token = storage.getItem('accessToken')
  try {
    await axios.post(
      `${SESSION_API_BASE_URL}/auth/session/logout`,
      {},
      {
        withCredentials: true,
        headers: { ...SESSION_HEADER, ...(token ? { 'X-Vera-Token': `${token}` } : {}) },
      },
    )
  } catch {
    // The server may be unreachable; the caller still clears local state.
  }
}
